import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import api, { BACKEND } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Sparkles, Trophy, Users, Share2, Filter } from 'lucide-react';

const RARITY_COLORS = {
  common: { bg: '#848E9C', label: 'Common' },
  rare: { bg: '#0ECB81', label: 'Rare' },
  epic: { bg: '#B26BFF', label: 'Epic' },
  legendary: { bg: '#FCD535', label: 'Legendary' },
  bronze: { bg: '#CD7F32', label: 'Bronze' },
  silver: { bg: '#C0C0C0', label: 'Silver' },
  gold: { bg: '#FCD535', label: 'Gold' },
};

function NFTCard({ nft }) {
  const isPersonal = nft.kind === 'referral_milestone';
  const r = RARITY_COLORS[nft.rarity] || RARITY_COLORS.common;
  const backendUrl = BACKEND;
  const ogUrl = `${backendUrl}/api/nfts/${nft.nft_id}/og.png`;
  const share = () => {
    const text = isPersonal
      ? `🏆 Just unlocked my ${nft.tier_label} NFT on BITGONODE — ${nft.milestone_count} referrals and counting! Join me:`
      : `🏆 My ${r.label.toUpperCase()} milestone NFT — ${nft.tier_label} @ ${nft.threshold_usdt?.toLocaleString?.() || nft.threshold_usdt} USDT pool!`;
    const shareData = { title: 'BITGONODE NFT', text, url: ogUrl };
    if (navigator.share) {
      navigator.share(shareData).catch(() => {});
    } else {
      navigator.clipboard.writeText(`${text}\n${ogUrl}`);
      toast.success('Share text + OG image link copied');
    }
  };
  return (
    <div className="border bg-[#181A20] flex flex-col overflow-hidden transition-transform hover:scale-[1.02]"
         style={{ borderColor: r.bg + '55' }} data-testid={`nft-${nft.nft_id}`}>
      <div className="aspect-square grid place-items-center relative" style={{ background: `linear-gradient(135deg, ${r.bg}22, #0B0E11 60%)` }}>
        {isPersonal ? (
          <div className="flex flex-col items-center gap-1">
            <div className="text-7xl font-black" style={{ color: r.bg, filter: 'drop-shadow(0 4px 20px ' + r.bg + '99)' }}>
              {nft.milestone_count}
            </div>
            <div className="text-xs uppercase tracking-widest text-white/80 font-mono">Referrals</div>
          </div>
        ) : (
          <div className="text-8xl" style={{ filter: 'drop-shadow(0 4px 20px ' + r.bg + '99)' }}>
            {nft.art?.emoji || '🏆'}
          </div>
        )}
        <div className="absolute top-2 left-2 text-[10px] font-mono uppercase px-2 py-0.5" style={{ background: r.bg, color: '#0B0E11' }}>
          {r.label}
        </div>
        {nft.boosted_by && (
          <div className="absolute top-2 right-2 text-[9px] font-mono uppercase px-1.5 py-0.5 bg-[#FCD535]/20 text-[#FCD535] border border-[#FCD535]/40" title="Rarity boosted by a group member perk">
            🎁 Boosted
          </div>
        )}
        {isPersonal && (
          <div className="absolute top-2 right-2 text-[9px] font-mono uppercase px-1.5 py-0.5 bg-[#0ECB81]/20 text-[#0ECB81] border border-[#0ECB81]/40">
            Personal
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <div className="text-white font-semibold text-sm truncate">{nft.tier_label}</div>
          {isPersonal ? (
            <div className="text-[#0ECB81] font-mono text-xs">+{nft.reward_points_granted || 0}p</div>
          ) : (
            <div className="text-[#FCD535] font-mono text-xs">{nft.threshold_usdt?.toLocaleString?.()}u</div>
          )}
        </div>
        <div className="text-[11px] text-[#848E9C] truncate flex items-center gap-1">
          <Users className="w-3 h-3" />
          {isPersonal
            ? `${nft.owner_name || 'You'} · ${nft.milestone_count} referrals`
            : `${nft.group_name || 'Group'} · ${nft.member_count} members`}
        </div>
        <div className="text-[10px] text-[#5E6673] font-mono">{new Date(nft.minted_at).toLocaleDateString()}</div>
        <Button onClick={share} className="h-7 text-xs bg-[#2B3139] hover:bg-[#3B4149] text-white mt-1" data-testid={`share-nft-${nft.nft_id}`}>
          <Share2 className="w-3 h-3 mr-1" /> Share
        </Button>
      </div>
    </div>
  );
}

export default function NFTs() {
  const { user } = useAuth();
  const [tab, setTab] = useState(user ? 'mine' : 'public');
  const [rarity, setRarity] = useState('');
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = rarity ? `?rarity=${rarity}` : '';
      const url = tab === 'mine' ? `/nfts/mine${q}` : `/nfts/gallery${q}`;
      const r = await api.get(url);
      setNfts(r.data.nfts || []);
    } catch { setNfts([]); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, rarity]);

  return (
    <div className="min-h-screen bg-[#0B0E11] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="w-8 h-8 text-[#FCD535]" />
          <h1 className="font-display text-4xl font-bold">NFT Gallery</h1>
        </div>
        <p className="text-[#B7BDC6] text-sm mb-6">Milestone NFTs auto-minted when your referral group crosses a USDT threshold.</p>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="inline-flex border border-[#2B3139] p-0.5 bg-[#181A20]" data-testid="nft-tabs">
            {user && (
              <button onClick={() => setTab('mine')} className={`px-4 py-1.5 text-xs uppercase font-mono ${tab === 'mine' ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6]'}`} data-testid="nft-tab-mine">My NFTs</button>
            )}
            <button onClick={() => setTab('public')} className={`px-4 py-1.5 text-xs uppercase font-mono ${tab === 'public' ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6]'}`} data-testid="nft-tab-public">Public Gallery</button>
          </div>
          <div className="flex items-center gap-1.5 ml-auto" data-testid="rarity-filters">
            <Filter className="w-3.5 h-3.5 text-[#848E9C]" />
            <button onClick={() => setRarity('')} className={`text-xs px-2 py-1 font-mono uppercase ${!rarity ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6] hover:text-white'}`}>All</button>
            {Object.entries(RARITY_COLORS).map(([k, v]) => (
              <button key={k} onClick={() => setRarity(k)} className={`text-xs px-2 py-1 font-mono uppercase ${rarity === k ? 'text-[#0B0E11]' : 'text-[#B7BDC6] hover:text-white'}`}
                style={rarity === k ? { background: v.bg } : {}} data-testid={`rarity-${k}`}>{v.label}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#5E6673]">Loading…</div>
        ) : nfts.length === 0 ? (
          <div className="border border-dashed border-[#2B3139] p-16 text-center" data-testid="nft-empty">
            <Sparkles className="w-8 h-8 text-[#5E6673] mx-auto mb-3" />
            <div className="text-white font-semibold mb-1">No NFTs yet</div>
            <div className="text-sm text-[#848E9C]">
              {tab === 'mine' ? 'Join a referral group. When the group crosses 10K/50K/100K USDT in referrals, you mint a group NFT.' : 'No NFTs match this filter yet.'}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {nfts.map(n => <NFTCard key={n.nft_id} nft={n} />)}
          </div>
        )}
      </div>
    </div>
  );
}
